/**
 * Self-heal stale-bundle dynamic-import failures after a deploy.
 *
 * Vite hashes every code-split chunk filename. When the server deploys a new
 * build, any tab still running the old `index-*.js` references chunks that no
 * longer exist on the CDN; routing into a lazy()-wrapped module then rejects
 * with `TypeError: Failed to fetch dynamically imported module: …/<old>.js`.
 *
 * Why this isn't a window-level handler: the rejection from `import()` inside
 * a `lazy()` boundary is consumed by React's Suspense and re-thrown synchronously
 * inside the nearest error boundary's `componentDidCatch`. It never reaches
 * `window.error` / `unhandledrejection`. The recovery has to be wired into the
 * Sentry React `<ErrorBoundary>`'s `onError` callback instead — see
 * `ModuleErrorBoundary.tsx` and the top-level boundary in `main.tsx`.
 *
 * Recovery: detect the chunk-load message, set a one-shot sessionStorage flag,
 * hard-reload. The flag prevents an infinite loop if the post-reload build
 * also fails — second occurrence falls through to the boundary's normal
 * "Something went wrong" fallback.
 */

const RELOAD_FLAG = 'hq:chunk-load-reload';

/**
 * Exported so `sentry.ts` can spread these into `ignoreErrors` instead of
 * keeping a hand-copied second list. The two drifted once already: the CSS
 * pattern below was added here for the reload but not there, so RUBY-21
 * self-healed AND still reported. Add a pattern here and both behaviours
 * follow. Plan: docs/plans/2026-08-08-prod-audit-fixes.md
 */
export const CHUNK_LOAD_PATTERNS = [
  // Modern browsers throw TypeError with this exact wording when a
  // `import('./Foo.js')` returns a 404 / network failure. Vite's prod builds
  // emit this from every lazy() boundary.
  'Failed to fetch dynamically imported module',
  // Older / non-Chromium variants — same root cause, slightly different wording.
  'error loading dynamically imported module',
  // Vite preloads a lazy chunk's CSS alongside its JS; after a deploy the old hashed
  // `<chunk>.css` 404s and Vite's preload helper throws `Unable to preload CSS for …`.
  // Same stale-bundle root cause as above → same one-shot reload recovery (otherwise it
  // falls through to the "Something went wrong" boundary instead of self-healing).
  'Unable to preload CSS',
];

export function isChunkLoadFailure(reason: unknown): boolean {
  const message =
    typeof reason === 'string'
      ? reason
      : reason instanceof Error
        ? reason.message
        : null;
  if (!message) return false;
  return CHUNK_LOAD_PATTERNS.some((p) => message.includes(p));
}

/**
 * If `reason` looks like a stale-bundle chunk-load failure, set the one-shot
 * flag and reload. Idempotent — second call within the same session no-ops so
 * the user lands on the boundary's fallback instead of looping.
 *
 * Wire this from every `Sentry.ErrorBoundary`'s `onError` prop. Calling it on
 * non-chunk-load errors is safe (returns immediately).
 */
export function reloadOnceOnChunkFailure(reason: unknown): void {
  if (!isChunkLoadFailure(reason)) return;
  if (typeof window === 'undefined') return;
  try {
    if (window.sessionStorage.getItem(RELOAD_FLAG) === '1') return;
    window.sessionStorage.setItem(RELOAD_FLAG, '1');
  } catch {
    // Blocked storage (Safari private mode, locked-down WebViews): no loop
    // protection is available, so don't auto-reload — and never throw from
    // inside an ErrorBoundary onError, which would replace the recoverable
    // chunk error with a storage error.
    return;
  }
  window.location.reload();
}

/**
 * Clear the reload flag once the app has stabilized — call from `main.tsx`
 * after `createRoot.render()`. The clear is deferred 30s rather than
 * immediate: `render()` returns synchronously but lazy chunks load *after*
 * that, so an immediate clear would defeat the loop-prevention if the
 * post-reload build also has a missing chunk (we'd reload, clear, fail,
 * reload again, …). 30s comfortably outlasts every lazy boundary's first
 * navigation attempt; if no failure has fired by then, the build is healthy
 * and the flag should reset for next time.
 */
export function markChunkLoadHandlerHealthy(): void {
  if (typeof window === 'undefined') return;
  window.setTimeout(() => {
    window.sessionStorage.removeItem(RELOAD_FLAG);
  }, 30_000);
}
